import { ChronoUnit, LocalDate } from "@js-joda/core";
import {
  every_x_months,
  every_x_months_last_day,
  every_x_months_last_working_day,
  every_x_weeks,
  every_x_years,
  RecurrenceEngine,
  RecurrenceRule,
  specific_dates,
} from "./types-v2";
import { alignPayDate } from "./build-ledger";

type EveryXUnitsConfig = {
  startDate: LocalDate;
  interval: number;
  unit: ChronoUnit;
  align: (base: LocalDate) => LocalDate;
};

function everyXUnitsEngine(config: EveryXUnitsConfig): RecurrenceEngine {
  const { startDate, interval, unit, align } = config;

  return {
    firstOnOrAfter(from) {
      let d = align(startDate);

      if (!d.isBefore(from)) return d;

      // Fixed-size units (DAYS / WEEKS)
      if (unit === ChronoUnit.DAYS || unit === ChronoUnit.WEEKS) {
        const unitsBetween = startDate.until(from, unit);
        const jumps = Math.ceil(unitsBetween / interval);
        return align(startDate.plus(jumps * interval, unit));
      }

      // Variable-size units (MONTHS / YEARS)
      while (d.isBefore(from)) {
        d = align(d.plus(interval, unit));
      }
      return d;
    },

    next(after) {
      return align(after.plus(interval, unit));
    },
  };
}

function alignToWeekday(base: LocalDate, weekday: number): LocalDate {
  const current = base.dayOfWeek().value();
  const diff = (weekday - current + 7) % 7;
  return diff === 0 ? base : base.plusDays(diff);
}

function engineFor(
  rule: RecurrenceRule,
  anchorStart?: LocalDate,
): RecurrenceEngine {
  switch (rule.kind) {
    case every_x_weeks:
      return everyXUnitsEngine({
        startDate: anchorStart ?? LocalDate.now(),
        interval: rule.interval,
        unit: ChronoUnit.WEEKS,
        align: (d) => alignToWeekday(d, rule.weekday.value()),
      });

    case every_x_months:
    case every_x_months_last_working_day:
    case every_x_months_last_day:
      return everyXUnitsEngine({
        startDate: rule.recurrenceAnchorDate,
        interval: rule.interval,
        unit: ChronoUnit.MONTHS,
        align: (d) => alignPayDate(d, rule),
      });

    case every_x_years:
      return everyXUnitsEngine({
        startDate: rule.recurrenceAnchorDate,
        interval: rule.interval,
        unit: ChronoUnit.YEARS,
        align: (d) => {
          const year = d.year();
          const month = (rule as any).month;
          const dayOfMonth = (rule as any).dayOfMonth;
          const base = LocalDate.of(year, month, 1);
          return base.withDayOfMonth(
            Math.min(dayOfMonth, base.lengthOfMonth()),
          );
        },
      });

    case specific_dates: {
      const dates = [...rule.dates].sort((a, b) => a.compareTo(b));

      return {
        firstOnOrAfter(from: LocalDate) {
          return dates.find((d) => !d.isBefore(from)) ?? null;
        },

        next(after: LocalDate) {
          return dates.find((d) => d.isAfter(after)) ?? null;
        },
      };
    }

    default:
      throw new Error(`Unhandled rule: ${JSON.stringify(rule)}`);
  }
}
export function occurrencesBetween(
  rule: RecurrenceRule,
  from: LocalDate,
  to: LocalDate,
  anchorStart?: LocalDate,
): LocalDate[] {
  const engine = engineFor(rule, anchorStart);
  const results: LocalDate[] = [];

  let current = engine.firstOnOrAfter(from);

  while (current && !current.isAfter(to)) {
    results.push(current);
    current = engine.next(current);
  }

  return results;
}
